import { createAsyncThunk } from '@reduxjs/toolkit';

import { api } from '@/features/ChatBot/api';
import type {
  AnalysisDetail,
  AnalysisListItem,
  CreateAnalysisPayload,
  TokenUsage,
  UpdateAnalysisPayload,
} from '@/features/ChatBot/interfaces';

import { getActionPrefix } from '@/utils/helpers';

const actionPrefix = getActionPrefix('analyses');

export const createAnalysis = createAsyncThunk<AnalysisDetail, CreateAnalysisPayload>(
  `${actionPrefix}/createAnalysis`,
  async payload => {
    const { data } = await api.createAnalysis(payload);

    return data;
  }
);

export const getAnalysesList = createAsyncThunk<Array<AnalysisListItem>>(
  `${actionPrefix}/getAnalysesList`,
  async () => {
    let pageNumber = 1;
    let shouldFetch = true;
    const analysesList: Array<AnalysisListItem> = [];

    do {
      try {
        const { data } = await api.getAnalyses({ page: pageNumber });

        if (Array.isArray(data)) {
          analysesList.push(...data);
          shouldFetch = false;
        } else {
          analysesList.push(...(data?.results ?? []));
          pageNumber += 1;
          shouldFetch = !!data?.next;
        }
      } catch {
        shouldFetch = false;
      }
    } while (shouldFetch);

    return analysesList.sort((a, b) => b.id - a.id);
  }
);

export const getAnalysis = createAsyncThunk<AnalysisDetail, number>(
  `${actionPrefix}/getAnalysis`,
  async analysisId => {
    const { data } = await api.getAnalysis(analysisId);

    return data;
  }
);

export const updateAnalysis = createAsyncThunk<
  void,
  { analysisId: number; payload: UpdateAnalysisPayload }
>(`${actionPrefix}/updateAnalysis`, async ({ analysisId, payload }) => {
  await api.updateAnalysis(analysisId, payload);
});

export const deleteAnalysis = createAsyncThunk<number, number>(
  `${actionPrefix}/deleteAnalysis`,
  async analysisId => {
    await api.deleteAnalysis(analysisId);

    return analysisId;
  }
);

export const exportAnalysisPdf = createAsyncThunk<Blob, number>(
  `${actionPrefix}/exportAnalysisPdf`,
  async analysisId => {
    const { data } = await api.exportAnalysisPdf(analysisId);

    return data;
  }
);

export const fetchTokenUsage = createAsyncThunk<TokenUsage>(
  `${actionPrefix}/fetchTokenUsage`,
  async () => {
    const { data } = await api.getTokenUsage();

    return data;
  }
);
